// ════════════════════════════════════════════════════════════════════════════
//  CajaDeTranscripcion.jsx
//  PEGAS A LA IZQUIERDA, Y A LA DERECHA VES LO QUE NSL ENTENDIÓ
//
//  ── EL INTENTO ANTERIOR, Y POR QUÉ SE TIRÓ ─────────────────────────────────
//  La primera versión coloreaba el texto DENTRO de la caja: un `<pre>` pintado
//  debajo y el textarea encima con la letra transparente. En una captura de
//  seis líneas quedaba precioso. Con una letra de verdad se cayó entero, y Rob
//  lo dijo en una frase que vale más que cualquier métrica:
//
//      «Quedó espantoso… no puedo copiar y modificar».
//
//  Tenía tres fallos y los tres eran del enfoque, no del remate:
//    · Los números de línea no seguían al texto. Una línea larga ocupa dos
//      renglones, y la regla contaba renglones: a partir de la primera línea
//      larga, cada número señalaba a otra cosa.
//    · La regla ni siquiera se movía al bajar: el texto scrolleaba y los
//      números se quedaban.
//    · Y la selección era invisible, porque la letra de arriba es
//      transparente. De ahí el «no puedo copiar y modificar»: podía, pero sin
//      ver qué estaba seleccionando, que es no poder.
//
//  ── LO QUE SE HACE AHORA, Y POR QUÉ ES MEJOR ───────────────────────────────
//  Dos paneles. A la izquierda, un textarea NORMAL: seleccionar, copiar,
//  pegar, deshacer y el cursor funcionan como en cualquier caja de texto,
//  porque es una caja de texto y no un truco. A la derecha, **lo que NSL
//  entendió**: cada línea con su tiempo en una ficha, y las que no tienen
//  tiempo marcadas en rojo.
//
//  Y eso es más útil que colorear lo pegado, que era cosmético. La pregunta
//  real al pegar una letra no es «¿de qué color es esto?» sino **«¿se va a leer
//  bien?»** — y eso sólo se contesta enseñando el resultado. Si la derecha dice
//  34 de 37, ya se sabe que hay tres líneas que arreglar y cuáles son, antes de
//  pulsar nada.
// ════════════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import "./CajaDeTranscripcion.css";

//  Las mismas formas que reconoce el parser del servidor
//  (`backend-api/src/utils/transcript_parser.py`). Aquí sirven para ADELANTAR
//  el resultado, no para decidirlo: quien lee de verdad es el servidor al
//  pulsar «Parsear». `prueba_transcripcion.py` comprueba que los dos están de
//  acuerdo sobre los mismos ejemplos.
const TIEMPO = String.raw`\d{1,3}:\d{2}(?::\d{2})?(?:[.,]\d{1,3})?`;

const FORMAS = [
  { tipo: "flecha", re: new RegExp(`^(${TIEMPO})\\s*-->\\s*${TIEMPO}`) },
  { tipo: "lrc", re: new RegExp(`^\\[(${TIEMPO})\\]`) },
  { tipo: "parentesis", re: new RegExp(`^\\((${TIEMPO})\\)`) },
  { tipo: "youtube", re: new RegExp(`^(${TIEMPO})`) },
];

const DURACION = new RegExp(
  String.raw`^\d+\s*(?:minute|minuto|second|segundo)s?` +
  String.raw`(?:\s*,?\s*(?:y\s*)?\d+\s*(?:minute|minuto|second|segundo)s?)?`,
  "i",
);

const ACOTACION = /^[[(][^\])]{0,40}[\])]\s*$/;

/** De una línea cruda, el tiempo que lleva (si lleva) y su texto. */
function lee(linea) {
  const limpia = linea.trim();
  if (!limpia) return null;

  for (const { tipo, re } of FORMAS) {
    const m = limpia.match(re);
    if (!m) continue;
    let resto = limpia.slice(m[0].length);
    //  La etiqueta que YouTube duplica detrás del tiempo. Se quita aquí igual
    //  que la quita el servidor, o la vista previa enseñaría «14 seconds» como
    //  si fuera parte de la letra.
    resto = resto.replace(DURACION, "");
    return { tiempo: m[1], texto: resto.replace(/[♪♫]/g, "").trim(), tipo };
  }
  return { tiempo: null, texto: limpia.replace(/[♪♫]/g, "").trim(), tipo: null };
}

/** Segundos de un «0:14» o «00:01:02.500», para ordenarlos y pintarlos igual. */
function enSegundos(t) {
  if (!t) return null;
  const partes = t.replace(",", ".").split(":").map(Number);
  if (partes.some(Number.isNaN)) return null;
  return partes.length === 3
    ? partes[0] * 3600 + partes[1] * 60 + partes[2]
    : partes[0] * 60 + partes[1];
}

function comoReloj(seg) {
  if (seg === null) return "—";
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  const dec = Math.round((s % 1) * 100);
  return `${m}:${String(Math.floor(s)).padStart(2, "0")}`
    + (dec ? `.${String(dec).padStart(2, "0")}` : "");
}

export default function CajaDeTranscripcion({
  valor,
  alCambiar,
  placeholder,
  prefijo = "tx",
}) {
  const leidas = useMemo(() => {
    const crudas = [];
    for (const linea of (valor || "").split("\n")) {
      const l = lee(linea);
      if (!l) continue;
      //  Una línea sin tiempo Y sin texto no es nada; una sin tiempo pero con
      //  texto SÍ cuenta: es el segundo verso de la de arriba, y el servidor
      //  lo recoge. Enseñarla sin tiempo y sin alarma es lo honesto.
      if (!l.tiempo && !l.texto) continue;
      crudas.push(l);
    }

    //  ── EL TIEMPO ARRIBA Y EL TEXTO DEBAJO (14-10-2026) ───────────────
    //  El panel de YouTube se copia así: el tiempo en su propia línea y el
    //  texto en la siguiente.
    //
    //      0:00
    //      How are you today, Apollo?
    //
    //  Esta vista los contaba como DOS renglones: uno «con tiempo y sin
    //  texto» y otro «sin tiempo». Con un transcript de 57 subtítulos decía
    //  «57 de 114 con tiempo» y remataba con «las líneas sin tiempo se leen
    //  como continuación de la anterior: NSL les calcula uno».
    //
    //  Era mentira, y de la peor clase: el servidor empareja las dos líneas
    //  perfectamente y no calcula nada. Rob lo leyó, dio el transcript por
    //  roto y volvió a empezar. La vista previa está para dar confianza, así
    //  que una que asusta sin motivo es peor que ninguna.
    const fuera = [];
    for (let i = 0; i < crudas.length; i++) {
      const esta = crudas[i];
      const siguiente = crudas[i + 1];
      const soloTiempo = esta.tiempo && !esta.texto;
      const soloTexto = siguiente && !siguiente.tiempo && siguiente.texto;
      if (soloTiempo && soloTexto) {
        fuera.push({ ...esta, texto: siguiente.texto });
        i += 1;              // la de abajo ya se ha usado
        continue;
      }
      fuera.push(esta);
    }
    return fuera;
  }, [valor]);

  const conTiempo = leidas.filter((l) => l.tiempo).length;
  const sinNada = leidas.filter((l) => !l.tiempo && !l.texto).length;
  const vacio = leidas.length === 0;

  return (
    <div className="tx-dos" data-prueba={`${prefijo}-caja-transcripcion`}>

      {/* ── IZQUIERDA: UNA CAJA DE TEXTO Y NADA MÁS ────────────────────
          Sin capas por encima, sin números a un lado. Seleccionar, copiar,
          pegar y deshacer funcionan porque no hay nada entre el dedo y el
          campo. Lo único que se toca es que se lea cómodo: más interlineado,
          más contraste y una tipografía de ancho fijo, que es la que deja los
          tiempos alineados al mirar en vertical. */}
      <div className="tx-panel tx-panel-escribir">
        <div className="tx-panel-cabecera">
          <span className="tx-panel-titulo">Pega aquí la letra</span>
          <span className="tx-panel-nota">
            {valor ? `${(valor.match(/\n/g) || []).length + 1} renglones` : "vacío"}
          </span>
        </div>
        <textarea
          className="tx-escribir"
          value={valor}
          onChange={(e) => alCambiar(e.target.value)}
          placeholder={placeholder}
          spellCheck={false}
          data-prueba={`${prefijo}-textarea`}
        />
      </div>

      {/* ── DERECHA: LO QUE NSL ENTENDIÓ ───────────────────────────────── */}
      <div className="tx-panel tx-panel-leer">
        <div className="tx-panel-cabecera">
          <span className="tx-panel-titulo">Lo que se va a leer</span>
          {!vacio && (
            <span className={`tx-marcador ${conTiempo === 0 ? "tx-mal" : "tx-ok"}`}
                  data-prueba={`${prefijo}-cuenta`}>
              {conTiempo} de {leidas.length} con tiempo
            </span>
          )}
        </div>

        <div className="tx-lista">
          {vacio && (
            <p className="tx-vacio">
              Aquí verás cada línea con su tiempo en cuanto pegues algo.
            </p>
          )}

          {leidas.map((l, i) => {
            const seg = enSegundos(l.tiempo);
            const acot = ACOTACION.test(l.texto);
            return (
              <div key={i}
                   className={`tx-fila${l.tiempo ? "" : " tx-fila-sin-tiempo"}`}
                   data-prueba={`${prefijo}-fila`}>
                <span className={`tx-chapa${l.tiempo ? "" : " tx-chapa-vacia"}`}>
                  {l.tiempo ? comoReloj(seg) : "sin tiempo"}
                </span>
                <span className={`tx-texto${acot ? " tx-texto-acotacion" : ""}`}>
                  {l.texto || <em className="tx-texto-vacio">(sin texto)</em>}
                </span>
              </div>
            );
          })}
        </div>

        {/* El aviso va ABAJO y sólo cuando hace falta. Arriba del todo, una
            advertencia permanente se vuelve parte del decorado. */}
        {!vacio && conTiempo === 0 && (
          <p className="tx-aviso">
            Ninguna línea trae tiempo. Copia la transcripción con sus marcas, o
            el subtítulo <strong>.srt</strong> / <strong>.vtt</strong> del vídeo.
          </p>
        )}
        {!vacio && conTiempo > 0 && conTiempo < leidas.length && (
          <p className="tx-aviso tx-aviso-suave">
            Las líneas sin tiempo se leen como continuación de la anterior: NSL
            les calcula uno y las marca para que las revises.
          </p>
        )}
        {sinNada > 0 && (
          <p className="tx-aviso tx-aviso-suave">{sinNada} líneas vacías, que se ignoran.</p>
        )}
      </div>
    </div>
  );
}
